import React, { useEffect, useMemo, useState } from "react";
import styles from "./inventory-report.style.module.scss";
import { useTranslation } from "react-i18next";
import { getInventoryDailySummary, getInventoryReportRows } from "../../requests/report.request";

type PageResp<T> = {
    content: T[];
    totalPages: number;
    totalElements: number;
    number: number;
    size: number;
    numberOfElements: number;
    first: boolean;
    last: boolean;
    empty: boolean;
};

type InventoryRowDto = {
    id: number;
    dateTimeUtc: string;
    tankId: number;
    kpuId: number;
    appObjectId: number;
    prodName: string | null;
    volume: number;
    mass: number;
    prodLevel: number;
    temp: number;
    prodDensity: number;
    sent: boolean;
    acceptedByQoldau: boolean;
    qoldauPackageId: string | null;
    errorMessageWhenSend: string | null;
    errorMessageCheckStatus: string | null;
    oilProductType: string | null;
    massUnitType: string | null;
    volumeUnitType: string | null;
    levelUnitType: string | null;
};

type InventoryDailySummaryDto = {
    tankId: number;
    appObjectId: number;
    day: string;
    avgVolume: number | null;
    minVolume: number | null;
    maxVolume: number | null;
    lastReadingTime: string | null;
};

function toIsoOrNull(dtLocal: string | null): string | undefined {
    if (!dtLocal || !dtLocal.trim()) return undefined;
    const d = new Date(dtLocal);
    if (isNaN(d.getTime())) return undefined;
    return d.toISOString();
}

export default function InventoryReportPage() {
    const { t } = useTranslation();

    const [fromLocal, setFromLocal] = useState<string>("");
    const [toLocal, setToLocal] = useState<string>("");

    const [tankId, setTankId] = useState<string>("");
    const [kpuId, setKpuId] = useState<string>("");
    const [appObjectId, setAppObjectId] = useState<string>("");

    const [sent, setSent] = useState<string>("");
    const [acceptedByQoldau, setAcceptedByQoldau] = useState<string>("");

    const [page, setPage] = useState<number>(0);
    const [size, setSize] = useState<number>(20);

    const [loading, setLoading] = useState(false);
    const [dailyLoading, setDailyLoading] = useState(false);

    const [resp, setResp] = useState<PageResp<InventoryRowDto> | null>(null);
    const [daily, setDaily] = useState<InventoryDailySummaryDto[]>([]);
    const [error, setError] = useState<string>("");

    const params = useMemo(() => {
        return {
            from: toIsoOrNull(fromLocal),
            to: toIsoOrNull(toLocal),
            tankId: tankId ? Number(tankId) : undefined,
            kpuId: kpuId ? Number(kpuId) : undefined,
            appObjectId: appObjectId ? Number(appObjectId) : undefined,
            sent: sent === "" ? undefined : sent === "true",
            acceptedByQoldau: acceptedByQoldau === "" ? undefined : acceptedByQoldau === "true",
            page,
            size,
            sort: "dateTimeUtc,desc",
        };
    }, [fromLocal, toLocal, tankId, kpuId, appObjectId, sent, acceptedByQoldau, page, size]);

    async function loadRows() {
        setLoading(true);
        setError("");
        try {
            const r = await getInventoryReportRows(params);
            setResp(r);
        } catch (e: any) {
            setError(e?.message ?? "Failed to load report");
            setResp(null);
        } finally {
            setLoading(false);
        }
    }

    async function loadDaily() {
        setDailyLoading(true);
        try {
            const r = await getInventoryDailySummary({
                from: params.from,
                to: params.to,
                tankId: params.tankId,
                kpuId: params.kpuId,
                appObjectId: params.appObjectId,
            });
            setDaily(r);
        } catch {
            setDaily([]);
        } finally {
            setDailyLoading(false);
        }
    }

    useEffect(() => {
        void loadRows();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params.page, params.size, params.sort]);

    function applyFilters() {
        setPage(0);
        void loadRows();
        void loadDaily();
    }

    function resetFilters() {
        setFromLocal("");
        setToLocal("");
        setTankId("");
        setKpuId("");
        setAppObjectId("");
        setSent("");
        setAcceptedByQoldau("");
        setPage(0);
        setSize(20);
        setDaily([]);
        setError("");

        setTimeout(() => {
            void loadRows();
            void loadDaily();
        }, 0);
    }

    const rows = resp?.content ?? [];

    return (
        <div className={styles.reportPage}>
            <div className={styles.headerRow}>
                <h2 className={styles.title}>{t("report.inventory.title")}</h2>

                <div className={styles.actions}>
                    <button className={styles.btn} onClick={applyFilters} disabled={loading}>
                        {t("common.apply")}
                    </button>
                    <button className={styles.btnSecondary} onClick={resetFilters} disabled={loading}>
                        {t("common.reset")}
                    </button>
                </div>
            </div>

            <div className={styles.filters}>
                <div className={styles.filterItem}>
                    <label>{t("common.from")}</label>
                    <input type="datetime-local" value={fromLocal} onChange={(e) => setFromLocal(e.target.value)} />
                </div>

                <div className={styles.filterItem}>
                    <label>{t("common.to")}</label>
                    <input type="datetime-local" value={toLocal} onChange={(e) => setToLocal(e.target.value)} />
                </div>

                <div className={styles.filterItem}>
                    <label>{t("report.inventory.appObjectId")}</label>
                    <input value={appObjectId} onChange={(e) => setAppObjectId(e.target.value)} placeholder="1" />
                </div>

                <div className={styles.filterItem}>
                    <label>{t("report.inventory.kpuId")}</label>
                    <input value={kpuId} onChange={(e) => setKpuId(e.target.value)} placeholder="1" />
                </div>

                <div className={styles.filterItem}>
                    <label>{t("report.inventory.tankId")}</label>
                    <input value={tankId} onChange={(e) => setTankId(e.target.value)} placeholder="2" />
                </div>

                <div className={styles.filterItem}>
                    <label>{t("report.inventory.sent")}</label>
                    <select value={sent} onChange={(e) => setSent(e.target.value)}>
                        <option value="">{t("common.all")}</option>
                        <option value="true">{t("common.yes")}</option>
                        <option value="false">{t("common.no")}</option>
                    </select>
                </div>

                <div className={styles.filterItem}>
                    <label>{t("report.inventory.acceptedByQoldau")}</label>
                    <select value={acceptedByQoldau} onChange={(e) => setAcceptedByQoldau(e.target.value)}>
                        <option value="">{t("common.all")}</option>
                        <option value="true">{t("common.yes")}</option>
                        <option value="false">{t("common.no")}</option>
                    </select>
                </div>

                <div className={styles.filterItem}>
                    <label>{t("common.pageSize")}</label>
                    <select value={String(size)} onChange={(e) => setSize(Number(e.target.value))}>
                        <option value="10">10</option>
                        <option value="20">20</option>
                        <option value="50">50</option>
                        <option value="100">100</option>
                    </select>
                </div>
            </div>

            {error ? <div className={styles.error}>{error}</div> : null}

            <div className={styles.sectionTitleRow}>
                <h3>{t("report.inventory.dailySummary")}</h3>
                {dailyLoading ? <span className={styles.muted}>{t("common.loading")}</span> : null}
            </div>

            <div className={styles.dailyGrid}>
                {(daily ?? []).slice(0, 6).map((d) => (
                    <div className={styles.dailyCard} key={`${d.tankId}-${d.day}`}>
                        <div className={styles.dailyTop}>
                            <div className={styles.dailyDay}>{d.day}</div>
                            <div className={styles.dailyMeta}>
                                {t("report.inventory.tankId")}: {d.tankId} · {t("report.inventory.appObjectId")}: {d.appObjectId}
                            </div>
                        </div>

                        <div className={styles.dailyBody}>
                            <div>
                                <div className={styles.muted}>{t("report.inventory.avgVolume")}</div>
                                <div className={styles.big}>{d.avgVolume ?? "-"}</div>
                            </div>
                            <div>
                                <div className={styles.muted}>{t("report.inventory.minMax")}</div>
                                <div className={styles.big}>
                                    {(d.minVolume ?? "-")} / {(d.maxVolume ?? "-")}
                                </div>
                            </div>
                        </div>

                        <div className={styles.dailyFooter}>
                            <span className={styles.muted}>{t("report.inventory.lastReading")}:</span>{" "}
                            {d.lastReadingTime ? new Date(d.lastReadingTime).toLocaleString() : "-"}
                        </div>
                    </div>
                ))}
            </div>

            <div className={styles.sectionTitleRow}>
                <h3>{t("report.inventory.rows")}</h3>
                {loading ? <span className={styles.muted}>{t("common.loading")}</span> : null}
            </div>

            <div className={styles.tableWrap}>
                <table className={styles.table}>
                    <thead>
                    <tr>
                        <th>{t("report.inventory.date")}</th>
                        <th>{t("report.inventory.appObjectId")}</th>
                        <th>{t("report.inventory.kpuId")}</th>
                        <th>{t("report.inventory.tankId")}</th>
                        <th>{t("report.inventory.volume")}</th>
                        <th>{t("report.inventory.mass")}</th>
                        <th>{t("report.inventory.temp")}</th>
                        <th>{t("report.inventory.sent")}</th>
                        <th>{t("report.inventory.acceptedByQoldau")}</th>
                        <th>{t("report.inventory.errorWhenSend")}</th>
                        <th>{t("report.inventory.errorCheckStatus")}</th>
                    </tr>
                    </thead>

                    <tbody>
                    {rows.length === 0 ? (
                        <tr>
                            <td colSpan={11} className={styles.emptyCell}>
                                {t("common.noData")}
                            </td>
                        </tr>
                    ) : (
                        rows.map((r) => (
                            <tr key={r.id}>
                                <td>{new Date(r.dateTimeUtc).toLocaleString()}</td>
                                <td>{r.appObjectId}</td>
                                <td>{r.kpuId}</td>
                                <td>{r.tankId}</td>
                                <td>{r.volume}</td>
                                <td>{r.mass}</td>
                                <td>{r.temp}</td>
                                <td>{r.sent ? t("common.yes") : t("common.no")}</td>
                                <td>{r.acceptedByQoldau ? t("common.yes") : t("common.no")}</td>
                                <td>{r.errorMessageWhenSend ?? ""}</td>
                                <td>{r.errorMessageCheckStatus ?? ""}</td>
                            </tr>
                        ))
                    )}
                    </tbody>
                </table>
            </div>

            <div className={styles.pager}>
                <div className={styles.pagerLeft}>
                    <button
                        className={styles.btnSecondary}
                        disabled={loading || !resp || resp.first}
                        onClick={() => setPage((p) => Math.max(0, p - 1))}
                    >
                        {t("common.prev")}
                    </button>
                    <button
                        className={styles.btnSecondary}
                        disabled={loading || !resp || resp.last}
                        onClick={() => setPage((p) => p + 1)}
                    >
                        {t("common.next")}
                    </button>
                </div>

                <div>
                    {resp ? (
                        <span className={styles.muted}>
              {t("common.page")} {resp.number + 1} / {resp.totalPages} · {t("common.total")}: {resp.totalElements}
            </span>
                    ) : null}
                </div>
            </div>
        </div>
    );
}
